import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";

import EarningsSummaryContainer from "./EarningsSummary.styles";
import GimTable from "components/Common/Table/Table";
import Select from "components/Common/Select/Select";
import PageTitle from "components/Common/PageTitle/PageTitle";
import { useBQMQ } from "context/BQMQ.context";
import * as Database from "utils/database.utils";
import { MetricRecord } from "./types";

const SCORE_METRIC_NAME = "_Score__1_5__vs_GIM_expectations";

const formatValue = (value: number | null): string => {
    if (value === null) return "";
    return parseFloat(value.toFixed(2)).toString();
};

const getScoreClass = (value: number): string => `score-${value}`;

const buildPivotedRows = (
    records: MetricRecord[],
    quarters: string[]
): any[] => {
    const metricOrder: { metric_num: string; metric_name: string }[] = [];
    const seen = new Set<string>();

    records.forEach((r) => {
        const key = `${r.metric_num}__${r.metric_name}`;
        if (!seen.has(key)) {
            seen.add(key);
            metricOrder.push({
                metric_num: r.metric_num,
                metric_name: r.metric_name,
            });
        }
    });

    const valueMap: Record<string, number | null> = {};
    records.forEach((r) => {
        valueMap[`${r.metric_num}__${r.metric_name}__${r.quarter_label}`] =
            r.value;
    });

    return metricOrder.map((m) => {
        const isEmpty = !m.metric_name || !m.metric_name.trim();
        const row: any = {
            metric_name: isEmpty ? "\u00A0" : m.metric_name,
            is_empty: isEmpty,
        };
        quarters.forEach((q) => {
            row[q] = isEmpty
                ? null
                : valueMap[`${m.metric_num}__${m.metric_name}__${q}`] ?? null;
        });
        return row;
    });
};

const EarningsSummary: React.FC = () => {
    const location = useLocation();
    const history = useHistory();
    const { companies, bqmqLoading } = useBQMQ();

    const companyOptions = useMemo(
        () =>
            companies.map((c: BQMQDropdownCompany) => ({
                label: c.label,
                value: String(c.value),
            })),
        [companies]
    );

    const handleCompanyChange = (value: string | number) => {
        if (!value) return;
        const params = new URLSearchParams(location.search);
        params.set("company_id", String(value));
        history.push(`${location.pathname}?${params.toString()}`);
    };

    const [snapshotDates, setSnapshotDates] = useState<string[]>([]);
    const [selectedVersion, setSelectedVersion] = useState<string>("");
    const [records, setRecords] = useState<MetricRecord[]>([]);
    const [loadingDates, setLoadingDates] = useState(false);
    const [loadingData, setLoadingData] = useState(false);

    const companyId = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get("company_id") ?? "";
    }, [location.search]);

    const selectedCompany = useMemo(
        () =>
            companyId
                ? companies.find(
                      (c: BQMQDropdownCompany) => String(c.value) === companyId
                  ) ?? null
                : null,
        [companyId, companies]
    );

    const companyName = selectedCompany?.label ?? "";

    useEffect(() => {
        if (!companyId) {
            setSnapshotDates([]);
            setSelectedVersion("");
            setRecords([]);
            return;
        }
        setLoadingDates(true);
        Database.get(
            `api/company/earnings-summary/snapshot-dates?company_id=${encodeURIComponent(
                companyId
            )}`
        )
            .then(async (res) => {
                if (res.ok) {
                    const json = await res.json();
                    const dates: string[] = json.data ?? [];
                    setSnapshotDates(dates);
                    setSelectedVersion(dates[0] ?? "");
                }
            })
            .finally(() => setLoadingDates(false));
    }, [companyId]);

    useEffect(() => {
        if (!companyId || !selectedVersion) {
            setRecords([]);
            return;
        }
        setLoadingData(true);
        Database.get(
            `api/company/earnings-summary?company_id=${encodeURIComponent(
                companyId
            )}&as_of_date=${selectedVersion}`
        )
            .then(async (res) => {
                if (res.ok) {
                    const json = await res.json();
                    setRecords(json.data ?? []);
                }
            })
            .finally(() => setLoadingData(false));
    }, [companyId, selectedVersion]);

    const versionOptions = useMemo(
        () =>
            snapshotDates.map((d) => ({
                label: new Date(d).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                }),
                value: d,
            })),
        [snapshotDates]
    );

    const scoreValue = useMemo(() => {
        const scoreRecord = records.find(
            (r) =>
                r.metric_name === SCORE_METRIC_NAME &&
                (!r.metric_num || !r.metric_num.trim())
        );
        return scoreRecord?.value ?? null;
    }, [records]);

    const tableRecords = useMemo(
        () =>
            records.filter(
                (r) =>
                    !(
                        r.metric_name === SCORE_METRIC_NAME &&
                        (!r.metric_num || !r.metric_num.trim())
                    )
            ),
        [records]
    );

    const quarters = useMemo(() => {
        return Array.from(
            new Map(
                tableRecords.map(
                    (r) => [r.quarter_num, r.quarter_label] as [string, string]
                )
            ).entries()
        )
            .sort((a, b) => Number(a[0]) - Number(b[0]))
            .map(([, label]) => label);
    }, [tableRecords]);

    const columns: Column[] = useMemo(
        () => [
            {
                id: 0,
                title: "Metric",
                value: "metric_name",
                sortable: false,
                show: true,
                cell: (val: string) => val ?? "",
            },
            ...quarters.map((q, idx) => ({
                id: idx + 1,
                title: q,
                value: q,
                sortable: false,
                show: true,
                cell: (val: number | null) => formatValue(val),
            })),
        ],
        [quarters]
    );

    const rows = useMemo(
        () => buildPivotedRows(tableRecords, quarters),
        [tableRecords, quarters]
    );

    const isLoading = loadingDates || loadingData;

    return (
        <EarningsSummaryContainer>
            <PageTitle
                title={
                    <span className="titleWithScore">
                        {companyName || "EARNINGS SUMMARY"}
                        {companyName && scoreValue !== null && (
                            <span
                                className={`scoreBadge ${getScoreClass(
                                    scoreValue
                                )}`}
                                title="Score (1-5) vs GIM expectations"
                            >
                                {scoreValue}
                            </span>
                        )}
                    </span>
                }
                actions={
                    companyId && snapshotDates.length > 0 ? (
                        <div className="versionSelect">
                            <div className="versionLabel">Version</div>
                            <Select
                                filterName="Version"
                                value={selectedVersion}
                                setValue={(v: string | number) =>
                                    setSelectedVersion(String(v))
                                }
                                options={versionOptions}
                                loadingOptions={loadingDates}
                                showAll={false}
                                clearable={false}
                            />
                        </div>
                    ) : undefined
                }
            />

            {!companyId && (
                <>
                    <div className="noData">
                        <FontAwesomeIcon icon={faInfoCircle} fixedWidth />
                        <p>
                            Please{" "}
                            <span className="highlight">select a company</span>{" "}
                            to view{" "}
                            <span className="highlight">earnings summary</span>.
                        </p>
                    </div>
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <div style={{ width: "300px" }}>
                            <Select
                                filterName="Company"
                                value=""
                                setValue={handleCompanyChange}
                                options={companyOptions}
                                loadingOptions={!!bqmqLoading}
                                searchable
                                showAll={false}
                                clearable={false}
                            />
                        </div>
                    </div>
                </>
            )}

            {companyId && (
                <GimTable
                    data={rows}
                    columns={columns}
                    filterQuery=""
                    pagination={false}
                    loading={isLoading}
                    showEmptyIcon={false}
                    emptyText="No data available"
                />
            )}
        </EarningsSummaryContainer>
    );
};

export default EarningsSummary;
